import React, { useState, useMemo } from 'react';
import { InputLabel, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';

import t from 'utils/translate';
import {
    selectCompanySettings,
    selectAllowedOperationalStatusList,
} from 'modules/settings/stores/company-settings-slice-handler';
import TextInput from 'modules/shared/components/text-input';
import SelectInput from 'modules/shared/components/select-input';
import {
    updateAlias,
    updateCustomStatus,
    updateOperationalStatus,
    updateAvailability,
    selectAliasSaving,
    selectUserCustomStatusSaving,
    selectAvailabilitySaving,
    selectOperationalStatusSaving,
} from 'modules/settings/stores/user-settings-slice-handler';
import Preloader from 'modules/shared/components/preloader';
import { ALIAS, USER_CUSTOM_STATUS, OPERATIONAL_STATUS_OFF, AVAILABILITY } from 'constants/user-settings';

export default function SideBarUserInfoFields() {
    const companySettings = useSelector(selectCompanySettings);
    const aliasDataIsSaving = useSelector(selectAliasSaving);
    const userCustomStatusIsSaving = useSelector(selectUserCustomStatusSaving);
    const availabilityIsSaving = useSelector(selectAvailabilitySaving);
    const operationalStatusIsSaving = useSelector(selectOperationalStatusSaving);
    const sortedOperationalStatuses = useSelector(selectAllowedOperationalStatusList);

    const EDIT_ICON = 'edit-pen';
    const SUBMIT_ICON = 'status-sent';

    const [newAlias, setNewAlias] = useState(companySettings.Alias || '');
    const [newCustomStatus, setNewCustomStatus] = useState(companySettings.UserCustomStatus || '');
    const [aliasEditMode, setAliasEditMode] = useState(false);
    const [userCustomStatusEditMode, setUserCustomStatusEditMode] = useState(false);

    const submitAlias = async () => {
        const cleanAlias = newAlias.trim();
        if (!aliasEditMode && !checkAliasValidity(cleanAlias)) {
            return;
        }
        await updateAlias(cleanAlias);
        setNewAlias(cleanAlias);
    };

    const cancelAliasChange = () => {
        setNewAlias(companySettings.Alias || '');
        setAliasEditMode(false);
    };

    const cancelCustomStatusChange = () => {
        setNewCustomStatus(companySettings.UserCustomStatus || '');
        setUserCustomStatusEditMode(false);
    };

    const submitCustomStatus = async () => {
        if (!userCustomStatusEditMode && !checkCustomStatusValidity(newCustomStatus)) {
            return;
        }
        await updateCustomStatus(newCustomStatus);
    };

    const handleAvailabilityChange = async (e) => await updateAvailability(e.target.value);

    const handleOperationalStatusChange = async (e) => await updateOperationalStatus(e.target.value);

    const handleAliasChange = (e) => {
        let value = e.target.value;
        setNewAlias(value);
    };

    const handleCustomStatusChange = (e) => {
        let value = e.target.value;
        setNewCustomStatus(value);
    };

    const checkAliasValidity = (value) =>
        value !== companySettings.Alias && (!value.length || value.length >= ALIAS.MIN_LENGTH);

    const checkCustomStatusValidity = (value) => value !== companySettings.UserCustomStatus;

    const getAliasIcon = () => {
        if (companySettings.AllowAliasModificationsFromClient) {
            if (aliasEditMode) {
                return checkAliasValidity(newAlias) ? SUBMIT_ICON : null;
            }

            return EDIT_ICON;
        }

        return null;
    };

    const toggleAndSubmitAlias = async () => {
        if (aliasEditMode) {
            await submitAlias();
        }

        setAliasEditMode(!aliasEditMode);
    };

    const handleAliasBlur = () => {
        if (newAlias === companySettings.Alias) {
            setAliasEditMode(false);
        }
    };

    const getCustomStatusIcon = () => {
        if (userCustomStatusEditMode) {
            return checkCustomStatusValidity(newCustomStatus) ? SUBMIT_ICON : null;
        }

        return EDIT_ICON;
    };

    const toggleAndSubmitCustomStatus = async () => {
        if (userCustomStatusEditMode) {
            await submitCustomStatus();
        }

        setUserCustomStatusEditMode(!userCustomStatusEditMode);
    };

    const handleCustomStatusBlur = () => {
        if (newCustomStatus === companySettings.UserCustomStatus) {
            setUserCustomStatusEditMode(false);
        }
    };

    return (
        <section className="user-info-fields">
            {companySettings.AllowAlias && (
                <div className="info-field">
                    <InputLabel htmlFor="alias" className="info-label">
                        {t('settings_alias')}
                    </InputLabel>
                    {aliasDataIsSaving ? (
                        <Preloader />
                    ) : (
                        <>
                            {aliasEditMode ? (
                                <TextInput
                                    id="alias-editable"
                                    type="text"
                                    disabled={!companySettings.AllowAliasModificationsFromClient}
                                    maxLength={ALIAS.MAX_LENGTH}
                                    minLength={ALIAS.MAX_LENGTH}
                                    value={newAlias}
                                    icon={getAliasIcon()}
                                    iconClick={toggleAndSubmitAlias}
                                    placeholder={t('settings_alias_placeholder')}
                                    onChange={handleAliasChange}
                                    onBlur={handleAliasBlur}
                                    hideCharsLeft
                                    handleCancel={cancelAliasChange}
                                    handleSubmit={toggleAndSubmitAlias}
                                    data-test-id="changeAlias-editable"
                                    autoComplete="off"
                                />
                            ) : (
                                <TextInput
                                    id="alias"
                                    type="text"
                                    disabled={!companySettings.AllowAliasModificationsFromClient}
                                    maxLength={ALIAS.MAX_LENGTH}
                                    minLength={ALIAS.MAX_LENGTH}
                                    value={companySettings.Alias}
                                    icon={getAliasIcon()}
                                    iconClick={toggleAndSubmitAlias}
                                    placeholder={t('settings_alias_placeholder')}
                                    hideCharsLeft
                                    onClick={toggleAndSubmitAlias}
                                    data-test-id="changeAlias"
                                    autoComplete="off"
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            {companySettings.AllowUserModificationsFromClient && (
                <div className="info-field">
                    <InputLabel htmlFor="about" className="info-label">
                        {t('settings_availability')}
                    </InputLabel>
                    {userCustomStatusIsSaving ? (
                        <Preloader />
                    ) : (
                        <>
                            {userCustomStatusEditMode ? (
                                <TextInput
                                    id="about-editable"
                                    type="text"
                                    multiline
                                    maxLength={USER_CUSTOM_STATUS.MAX_LENGTH}
                                    value={newCustomStatus}
                                    icon={getCustomStatusIcon()}
                                    iconClick={toggleAndSubmitCustomStatus}
                                    placeholder={t('settings_status_placeholder')}
                                    onChange={handleCustomStatusChange}
                                    onBlur={handleCustomStatusBlur}
                                    handleCancel={cancelCustomStatusChange}
                                    handleSubmit={toggleAndSubmitCustomStatus}
                                    data-test-id="changeStatus-editable"
                                />
                            ) : (
                                <TextInput
                                    id="about"
                                    type="text"
                                    multiline
                                    maxLength={USER_CUSTOM_STATUS.MAX_LENGTH}
                                    value={companySettings.UserCustomStatus}
                                    icon={getCustomStatusIcon()}
                                    iconClick={toggleAndSubmitCustomStatus}
                                    placeholder={t('settings_status_placeholder')}
                                    hideCharsLeft
                                    onClick={toggleAndSubmitCustomStatus}
                                    data-test-id="changeStatus"
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            {companySettings.AllowUserAvailability && (
                <div className="info-field">
                    {availabilityIsSaving && <Preloader />}
                    <InputLabel htmlFor="availability" className="info-label">
                        {t('settings_status')}
                    </InputLabel>
                    <SelectInput
                        id="availability"
                        value={companySettings.UserAvailability}
                        handleChange={handleAvailabilityChange}
                        className="info-field-dropdown"
                        data-test-id="changeAvailability"
                    >
                        <MenuItem value={AVAILABILITY.ON_DUTY}>{t('js_all_user_availability_0')}</MenuItem>
                        <MenuItem value={AVAILABILITY.OFF_DUTY}>{t('js_all_user_availability_1')}</MenuItem>
                    </SelectInput>
                </div>
            )}
            {companySettings.AllowOperationalStatus && companySettings.OperationalStatusList.length > 0 && (
                <div className="info-field">
                    {operationalStatusIsSaving && <Preloader />}
                    <InputLabel htmlFor="operational-status" className="info-label">
                        {t('settings_operational_status')}
                    </InputLabel>
                    <SelectInput
                        id="operational-status"
                        value={companySettings.OperationalStatus || OPERATIONAL_STATUS_OFF}
                        handleChange={handleOperationalStatusChange}
                        className="info-field-dropdown"
                        data-test-id="changeOperationalStatus"
                    >
                        <MenuItem value={OPERATIONAL_STATUS_OFF}>{t('global_off')}</MenuItem>
                        {sortedOperationalStatuses.map(
                            (opStatus) =>
                                <MenuItem key={opStatus.OperationalStatusId} value={opStatus.OperationalStatusId}>
                                    {`${opStatus.Code}. ${opStatus.Label}`}
                                </MenuItem>
                        )}
                    </SelectInput>
                </div>
            )}
        </section>
    );
}
